import {useEffect, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import {usePageVisibility} from 'hooks/usePageVisibility';

/** Fraction of the observed element that must be on screen to count as viewed. */
export const DEFAULT_VIEW_THRESHOLD = 0.5;

/** How long the element must stay on screen before it counts as viewed. */
export const DEFAULT_DWELL_MS = 1000;

type UseIsViewedOptions = {
  threshold?: number;
  dwellMs?: number;
};

type UseIsViewedResult = {
  ref: (node?: Element | null) => void;
  isViewed: boolean;
};

/**
 * Whether someone is plausibly looking at an element: the tab is visible,
 * enough of the element is on screen, and both have held for `dwellMs`. The
 * wait ignores quick scroll-pasts and pages left straight away.
 *
 * Pick the narrowest hook for the question:
 *   - Is the tab on screen?                -> usePageVisibility
 *   - Is this element on screen right now? -> useInView (react-intersection-observer)
 *   - Has someone plausibly seen it?       -> useIsViewed
 *
 * If the element unmounts, `isViewed` goes back to false.
 *
 * @returns Object containing:
 *   - ref: Callback ref to attach to the element
 *   - isViewed: Whether the element is currently being viewed
 */
export const useIsViewed = ({
  threshold = DEFAULT_VIEW_THRESHOLD,
  dwellMs = DEFAULT_DWELL_MS,
}: UseIsViewedOptions = {}): UseIsViewedResult => {
  const isPageVisible = usePageVisibility();
  const {ref, inView} = useInView({threshold});
  const isOnScreen = isPageVisible && inView;
  const [isViewed, setIsViewed] = useState(false);

  useEffect(() => {
    if (!isOnScreen) {
      setIsViewed(false);
      return;
    }

    if (dwellMs <= 0) {
      setIsViewed(true);
      return;
    }

    const timer = setTimeout(() => setIsViewed(true), dwellMs);
    return () => clearTimeout(timer);
  }, [isOnScreen, dwellMs]);

  return {ref, isViewed};
};
